import { t } from 'i18next';
import { isNil } from 'lodash';
import * as Yup from 'yup';
import { TenantInfoType } from '../../../../../shared/types';

export const getTenantSchema = (tenantInfo?: TenantInfoType) => {
  const surveysPerMonth =
    tenantInfo?.survey_limit_info?.surveys_per_month ?? 5000;
  const surveysPerQuarter =
    tenantInfo?.survey_limit_info?.surveys_per_quarter ?? 15000;
  const surveysPerSemiannual =
    tenantInfo?.survey_limit_info?.surveys_per_semi_annual ?? 30000;
  const surveysPerYear =
    tenantInfo?.survey_limit_info?.surveys_per_year ?? 60000;
  return Yup.object({
    survey_limit_info: Yup.object({
      surveys_per_month: Yup.number()
        .nullable()
        .typeError(`${t('forms.err_msg.required_field')}`)
        .integer(`${t('forms.err_msg.decimal')}`)
        .max(
          surveysPerMonth,
          `${t('forms.err_msg.max_len', {
            len: `${surveysPerMonth}`,
          })}`,
        )
        .min(
          1,
          `${t('forms.err_msg.greater_than', {
            len: '0',
          })}`,
        ),
      surveys_per_quarter: Yup.number()
        .nullable()
        .typeError(`${t('forms.err_msg.required_field')}`)
        .integer(`${t('forms.err_msg.decimal')}`)
        .max(
          surveysPerQuarter,
          `${t('forms.err_msg.max_len', {
            len: `${surveysPerQuarter}`,
          })}`,
        )
        .min(
          1,
          `${t('forms.err_msg.greater_than', {
            len: '0',
          })}`,
        ),
      surveys_per_semi_annual: Yup.number()
        .nullable()
        .typeError(`${t('forms.err_msg.required_field')}`)
        .integer(`${t('forms.err_msg.decimal')}`)
        .max(
          surveysPerSemiannual,
          `${t('forms.err_msg.max_len', {
            len: `${surveysPerSemiannual}`,
          })}`,
        )
        .min(
          1,
          `${t('forms.err_msg.greater_than', {
            len: '0',
          })}`,
        ),
      surveys_per_year: Yup.number()
        .nullable()
        .typeError(`${t('forms.err_msg.required_field')}`)
        .integer(`${t('forms.err_msg.decimal')}`)
        .max(
          surveysPerYear,
          `${t('forms.err_msg.max_len', {
            len: `${surveysPerYear}`,
          })}`,
        )
        .min(
          1,
          `${t('forms.err_msg.greater_than', {
            len: '0',
          })}`,
        ),
    }),
    contact_settings: Yup.object({
      contact_limit: Yup.object({
        frequency_month: Yup.number()
          .nullable()
          .typeError(`${t('forms.err_msg.required_field')}`)
          .integer(`${t('forms.err_msg.decimal')}`)
          .max(
            30,
            `${t('forms.err_msg.max_len', {
              len: '30',
            })}`,
          )
          .min(
            1,
            `${t('forms.err_msg.greater_than', {
              len: '0',
            })}`,
          ),
        frequency_week: Yup.number()
          .nullable()
          .typeError(`${t('forms.err_msg.required_field')}`)
          .integer(`${t('forms.err_msg.decimal')}`)
          .max(
            10,
            `${t('forms.err_msg.max_len', {
              len: '10',
            })}`,
          )
          .min(
            1,
            `${t('forms.err_msg.greater_than', {
              len: '0',
            })}`,
          ),
        frequency_quarter: Yup.number()
          .nullable()
          .typeError(`${t('forms.err_msg.required_field')}`)
          .integer(`${t('forms.err_msg.decimal')}`)
          .max(
            90,
            `${t('forms.err_msg.max_len', {
              len: '90',
            })}`,
          )
          .min(
            1,
            `${t('forms.err_msg.greater_than', {
              len: '0',
            })}`,
          ),
      }).required(),
      recipient_list_in_hours: Yup.number()
        .typeError(`${t('forms.err_msg.required_field')}`)
        .integer(`${t('forms.err_msg.decimal')}`)
        .max(
          24,
          `${t('forms.err_msg.max_len', {
            len: '24',
          })}`,
        )
        .min(
          2,
          `${t('forms.err_msg.min_len', {
            len: '2',
          })}`,
        )
        .required(`${t('forms.err_msg.required_field')}`),
    }),
  }).required();
};
export const getTenantDefalutValues = (): TenantInfoType => ({
  survey_limit_info: {
    surveys_per_month: 5000,
    surveys_per_quarter: 15000,
    surveys_per_semi_annual: 30000,
    surveys_per_year: 60000,
  },
  contact_settings: {
    contact_limit: {
      frequency_month: 1,
      frequency_week: 1,
      frequency_quarter: 1,
    },
    recipient_list_in_hours: 2,
  },
});

export const getTenantFormValues = ({
  values,
}: {
  values: TenantInfoType;
}): TenantInfoType => {
  const tenantInfo: TenantInfoType = { ...values };
  const { survey_limit_info } = tenantInfo || {};
  const {
    surveys_per_quarter,
    surveys_per_month,
    surveys_per_semi_annual,
    surveys_per_year,
  } = survey_limit_info || {};
  if (!isNil(surveys_per_quarter)) {
    tenantInfo.survey_limit_info = {
      surveys_per_quarter: Number(surveys_per_quarter),
      surveys_per_month: null,
      surveys_per_semi_annual: null,
      surveys_per_year: null,
    };
  } else if (!isNil(surveys_per_semi_annual)) {
    tenantInfo.survey_limit_info = {
      surveys_per_quarter: null,
      surveys_per_semi_annual: Number(surveys_per_semi_annual),
      surveys_per_month: null,
      surveys_per_year: null,
    };
  } else if (!isNil(surveys_per_month)) {
    tenantInfo.survey_limit_info = {
      surveys_per_quarter: null,
      surveys_per_semi_annual: null,
      surveys_per_year: null,
      surveys_per_month: Number(surveys_per_month),
    };
  } else if (!isNil(surveys_per_year)) {
    tenantInfo.survey_limit_info = {
      surveys_per_quarter: null,
      surveys_per_semi_annual: null,
      surveys_per_month: null,
      surveys_per_year: Number(surveys_per_year),
    };
  }
  const { contact_settings } = tenantInfo;
  const { contact_limit } = contact_settings || {};
  const { frequency_month, frequency_week, frequency_quarter } =
    contact_limit || {};
  if (!isNil(frequency_month)) {
    tenantInfo.contact_settings = {
      ...(tenantInfo.contact_settings || {}),
      contact_limit: {
        frequency_month: Number(frequency_month),
        frequency_week: null,
        frequency_quarter: null,
      },
    };
  } else if (!isNil(frequency_week)) {
    tenantInfo.contact_settings = {
      ...(tenantInfo.contact_settings || {}),
      contact_limit: {
        frequency_month: null,
        frequency_week: Number(frequency_week),
        frequency_quarter: null,
      },
    };
  } else if (!isNil(frequency_quarter)) {
    tenantInfo.contact_settings = {
      ...(tenantInfo.contact_settings || {}),
      contact_limit: {
        frequency_month: null,
        frequency_quarter: Number(frequency_quarter),
        frequency_week: null,
      },
    };
  }
  return tenantInfo;
};

export const getTenantPayload = ({
  values = {},
}: {
  values: TenantInfoType;
}): TenantInfoType => {
  return getTenantFormValues({ values });
};
